import {DataStatus, Flag, Menu} from 'platform/components';
import {
  Box,
  Center,
  Clickable,
  Grid,
  HStack,
  Heading,
  Icon,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';

import {map, pluck, uniq} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useQueryState} from 'shared/web';

import {useGetVideosQuery} from '../../../api/academyApi';
import {SectionCard} from '../../../components/SectionCard';

export function VideosSection() {
  const {data: recordings, isLoading, isError} = useGetVideosQuery();
  const [activeMeetup, setActiveMeetup] = useQueryState('meetup');
  const [activeVideoId, setActiveVideoId, clearActiveVideoId] = useQueryState('video');

  const activeVideo = isNotNilOrEmpty(activeVideoId)
    ? recordings?.videos?.find((video) => video.id === activeVideoId)
    : null;

  const meetups = recordings?.meetups ?? [];
  const departments = uniq(pluck('department', meetups));
  const menuItems = map(
    (department) => ({
      id: department,
      label: department,
      items: meetups
        .filter((meetup) => meetup.department === department)
        .map((meetup) => ({id: meetup.name, label: meetup.name})),
    }),
    departments
  );

  const onMenuItemChange = (id: string) => {
    clearActiveVideoId();
    setActiveMeetup(id);
  };

  return (
    <SectionCard title="Videos" subtitle="Meetup recordings">
      <Box paddingTop={2} paddingHorizontal={4} overflow="hidden">
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          isEmpty={isNilOrEmpty(recordings?.videos)}
          minHeight={50}
        >
          <HStack spacing={6}>
            <Box minWidth={45} width="25%" maxWidth={75}>
              <Menu
                items={menuItems}
                activeItemId={activeMeetup ?? ''}
                onItemChange={onMenuItemChange}
                data-testid="meetups-menu"
              />
            </Box>

            <Box overflow="hidden" flex={1}>
              <VStack spacing={10}>
                <Show when={activeVideo}>
                  <Box flex={1} position="relative" overflow="hidden">
                    <VideoAspectRatio>
                      <VideoIframe
                        width="100%"
                        height="100%"
                        title={activeVideo?.name}
                        src={`https://www.youtube.com/embed/${activeVideo?.id}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen;"
                        frameBorder="0"
                      />
                    </VideoAspectRatio>
                  </Box>
                </Show>

                <Grid columns={[1, 1, 2]} spacing={6}>
                  {recordings?.videos
                    ?.filter((video) => !activeMeetup || video.meetup === activeMeetup)
                    .map((video) => (
                      <Clickable
                        key={video.id}
                        onClick={() => setActiveVideoId(video.id)}
                        isDisabled={video.id === activeVideoId}
                      >
                        <Box
                          opacity={video.id === activeVideoId ? 0.8 : 1}
                          pointerEvents={video.id === activeVideoId ? 'none' : 'auto'}
                        >
                          <StyledVideo>
                            <HStack spacing={4}>
                              <Box
                                width={50}
                                borderRadius="xSmall"
                                overflow="hidden"
                                position="relative"
                                flexShrink={0}
                              >
                                <img
                                  src={`https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`}
                                  alt={video.name}
                                />
                                <Show when={video.id === activeVideoId}>
                                  <Box
                                    position="absolute"
                                    left={0}
                                    right={0}
                                    top={0}
                                    bottom={0}
                                    backgroundColor="general.white"
                                    opacity={0.7}
                                  >
                                    <Center width="100%" height="100%">
                                      <Icon
                                        value="action/visibility"
                                        color="general.black"
                                        size={18}
                                      />
                                    </Center>
                                  </Box>
                                </Show>
                              </Box>

                              <VStack>
                                <Heading size={5}>{video.name}</Heading>
                                <Space vertical={2} />
                                <Text size="base">{video.presenter}</Text>
                                <Text size="base">{video.meetup}</Text>
                                <Space vertical={1} />
                                <Show when={video.length}>
                                  <Flag
                                    label={video.length!}
                                    colorScheme="neutral"
                                    isSubtle
                                    size="small"
                                  />
                                </Show>
                              </VStack>
                            </HStack>
                          </StyledVideo>
                        </Box>
                      </Clickable>
                    ))}
                </Grid>
              </VStack>
            </Box>
          </HStack>
        </DataStatus>
      </Box>
    </SectionCard>
  );
}

const StyledVideo = styled.div`
  &:hover {
    div[role='heading'] {
      text-decoration: underline;
    }
    img {
      filter: brightness(1.1) contrast(0.95);
    }
  }
`;

const VideoAspectRatio = styled.div`
  width: 100%;
  padding-bottom: 56.25%;
  position: relative;
`;

const VideoIframe = styled.iframe`
  width: 100%;
  height: 100%;
  position: absolute;
`;
